import { Building2 } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

/**
 * ☠️ **ESTE MAPA ESTÁ EN RETIRADA. No le agregues una quinta entrada.**
 *
 * Era la copia de back-office del que vivía en
 * `packages/client-app-domain/src/properties/project-mark.ts`, y el 2026-09-10 aquél se borró: la
 * marca de un proyecto pasó a ser un DATO suyo (`project.mark`, 1024×1024, cargado desde su
 * formulario) en vez de una constante compilada que sólo conocía cuatro códigos.
 *
 * Acá sobrevive como **respaldo transitorio con fecha de vencimiento**, y el motivo es concreto:
 * `ProjectBadge` lo dibujan unas veinte pantallas —inventario, créditos, entregas, negociaciones,
 * inmuebles, subtipos— y **ninguno de esos seis contratos publica `mark` todavía**. Borrarlo hoy
 * dejaría las veinte con el ícono genérico de edificio, que es una regresión visible que nadie
 * pidió, y que además no se cura subiendo los logos: se cura ensanchando seis contratos, que es
 * trabajo de otro carril.
 *
 * | Para borrarlo hace falta | |
 * |---|---|
 * | 1 | Que los seis contratos de listado publiquen `project.mark` (aditivo, como `address`) |
 * | 2 | Que cada pantalla se lo pase a `ProjectBadge` |
 * | 3 | Que los cuatro proyectos tengan su logo cargado |
 *
 * Mientras tanto: **`mark` gana siempre que llegue**, así que una pantalla que ya lo publique usa
 * el logo real del proyecto y no esta lista. Es lo que hace que el respaldo se apague solo, pantalla
 * por pantalla, en vez de todo de golpe.
 */
const LEGACY_PROJECT_LOGOS = {
  CA: 'https://assets.elevenestudio.me/F8DFD950-PROJECT%3DCADIZ.png',
  AE: 'https://assets.elevenestudio.me/749C8257-PROJECT%3DENCANTO.png',
  AS: 'https://assets.elevenestudio.me/37531669-PROJECT%3DSERENA.png',
  AV: 'https://assets.elevenestudio.me/06890956-PROJECT%3DVARENA.png'
} as const

type ProjectIdentityProps = {
  name?: string | null
  /**
   * El logo cuadrado del proyecto (`project.mark`). **Es la fuente buena.**
   *
   * Opcional porque la mayoría de los listados todavía no lo publica en su contrato; cuando lo
   * pasan, gana sobre el respaldo de arriba.
   */
  mark?: string | null
  /** Sólo para el respaldo transitorio. Ver el docblock del mapa. */
  code?: string | null
}

type ProjectLogoProps = ProjectIdentityProps & {
  className?: string
}

/** La marca a dibujar: primero el dato del proyecto, después el respaldo con fecha. */
function logoOf({ mark, code }: ProjectIdentityProps): string | undefined {
  // Una cadena vacía es ausencia, no una URL: en la base hay proyectos con `image: ''` y el mismo
  // descuido puede repetirse acá.
  const own = mark?.trim()
  if (own) return own

  return code ? LEGACY_PROJECT_LOGOS[code as keyof typeof LEGACY_PROJECT_LOGOS] : undefined
}

export function ProjectLogo({ name, mark, code, className }: ProjectLogoProps) {
  const logo = logoOf({ mark, code })

  if (!logo) {
    return (
      <span
        aria-hidden="true"
        className={cn(
          'flex h-5 w-5 shrink-0 items-center justify-center rounded bg-background/80 text-muted-foreground',
          className
        )}
      >
        <Building2 className="h-3.5 w-3.5" />
      </span>
    )
  }

  return (
    <img
      src={logo}
      alt=""
      width={20}
      height={20}
      loading="lazy"
      decoding="async"
      title={name ? `Proyecto ${name}` : undefined}
      className={cn('h-5 w-5 shrink-0 rounded object-cover', className)}
    />
  )
}

type ProjectBadgeProps = ProjectIdentityProps & {
  className?: string
}

export function ProjectBadge({ name, mark, code, className }: ProjectBadgeProps) {
  if (!name) return <span className="text-muted-foreground">—</span>

  return (
    <span
      className={cn(
        'flex w-fit max-w-full min-w-0 items-center gap-1 rounded bg-secondary py-1 pr-2 pl-1 dark:bg-accent',
        className
      )}
    >
      <ProjectLogo name={name} mark={mark} code={code} />
      <span
        className="min-w-0 truncate text-sm font-medium text-secondary-foreground dark:text-neutral-400"
        title={name}
      >
        {name}
      </span>
    </span>
  )
}
